import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import MapView from "@/components/MapView";
import PODCapture from "@/components/PODCapture";
import { StopFixActions, fixDeadline } from "@/components/StopFixActions";
import { Navigation, Phone, MessageSquare, Play, Pause, CheckCircle2, XCircle, MapPin, Camera, X, Info, Snowflake, Flag, ArrowLeft } from "lucide-react";
import { fmtTime, fmtDateTime } from "@/lib/datetime";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";
import { fmtDistance, fmtEta } from "@/lib/units";
import {
  enqueue as queuePOD,
  attachUploader,
  startSweeper,
  drain as drainQueue,
  subscribe as subscribeQueue,
  subscribeGraveyard as subscribeGraveyardQueue,
  partsToFormData,
} from "@/lib/podQueue";
import { CloudUpload, AlertTriangle, PenLine } from "lucide-react";

export default function Driver() {
  const { routeId } = useParams();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [active, setActive] = useState(null);
  const [orders, setOrders] = useState([]);
  const [podOrder, setPodOrder] = useState(null);
  const [failOrder, setFailOrder] = useState(null);
  const [failReason, setFailReason] = useState("");
  const [failPhoto, setFailPhoto] = useState(null);
  const [failPhotoPreview, setFailPhotoPreview] = useState(null);
  const [failSubmitting, setFailSubmitting] = useState(false);
  const [shareLoc, setShareLoc] = useState(true);
  const [myLoc, setMyLoc] = useState(null);
  const watchIdRef = useRef(null);

  // Optimistic UI cache for POD / Fail submissions. When the driver hits
  // "Complete delivery" we close the sheet IMMEDIATELY and stamp the stop
  // here so the card greys out + the map marker turns green/red right away,
  // while the (potentially slow) photo + signature upload runs in the
  // background. Once the server confirms, the regular /orders poll will
  // carry the same status and this cache becomes a no-op. On failure we
  // remove the entry and toast the driver to retry.
  // Shape: { [order_id]: { status: "delivered" | "failed", at: epoch_ms } }
  //
  // PERSISTED to localStorage so it survives page reloads / iOS Safari
  // backgrounding — otherwise the driver could see the green checkmark
  // vanish after refresh mid-upload and think the delivery was lost.
  // Entries auto-expire after 24 h (safety net against a broken sync loop).
  const OPTIMISTIC_KEY = "pr:driver:optimistic:v1";
  const OPTIMISTIC_TTL_MS = 24 * 60 * 60 * 1000;
  const [optimistic, setOptimistic] = useState(() => {
    try {
      const raw = window.localStorage?.getItem(OPTIMISTIC_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return {};
      const now = Date.now();
      const fresh = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (v && typeof v === "object" && v.status && (now - (v.at || 0)) < OPTIMISTIC_TTL_MS) {
          fresh[k] = v;
        }
      }
      return fresh;
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try {
      window.localStorage?.setItem(OPTIMISTIC_KEY, JSON.stringify(optimistic));
    } catch { /* quota / private mode — best-effort */ }
  }, [optimistic]);
  const [uploading, setUploading] = useState({}); // { [order_id]: percent 1-99 } — UI badge
  // Items currently sitting in IndexedDB waiting (or retrying) to upload.
  // Drives the "N pending uploads" pill at the top of the page.
  const [pendingQ, setPendingQ] = useState([]);

  // ─── 3:30 PM ET Clinic-still-open warning ────────────────────────────
  // Between 15:30 – 15:59 (America/New_York), Monday–Friday, if the active
  // route is in progress AND has any stop with priority="clinic" that is
  // NOT delivered / not picked-up / not terminally failed, pop a modal that
  // the driver must acknowledge. Dismiss persists in localStorage keyed by
  // ET date + route id so the alert fires ONCE per day per route, and comes
  // back if the driver switches to a different route.
  const [clinicWarn, setClinicWarn] = useState({ open: false, stops: [] });

  const markOptimistic = (orderId, status) =>
    setOptimistic((m) => ({ ...m, [orderId]: { status, at: Date.now() } }));
  const clearOptimistic = (orderId) =>
    setOptimistic((m) => { const n = { ...m }; delete n[orderId]; return n; });
  const setUploadingFlag = (orderId, val) =>
    setUploading((m) => {
      const n = { ...m };
      // val === false | null | 0 | undefined → clear the badge for this order.
      // val === true → show badge with unknown percent (rendered as spinner-only).
      // val === number 1..99 → show badge with live percent.
      if (val === false || val == null || val === 0) delete n[orderId];
      else n[orderId] = val;
      return n;
    });

  // ============ Offline-tolerant POD/Fail queue =====================
  // The uploader is the single source of truth for actually POSTing a queued
  // submission. It is registered once and re-fired by the queue sweeper
  // (every 30 s) and on `online` / `visibilitychange` events.
  // We thread the current `load()` through a ref so the uploader closure
  // doesn't go stale across renders.
  const loadRef = useRef(null);

  useEffect(() => {
    const uploader = async (item) => {
      // Start the badge at 1% so drivers immediately see the pill move off
      // "starting…". onUploadProgress will overwrite this as bytes flush.
      setUploadingFlag(item.order_id, 1);
      try {
        const fd = partsToFormData(item.parts);
        await api.post(item.endpoint, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          // Live upload progress so the driver sees the POD pill move
          // instead of a static "uploading proof…" that looks stuck on
          // weak LTE. We cap at 99 here — the badge clears on success.
          onUploadProgress: (evt) => {
            if (evt && evt.total) {
              const pct = Math.round((evt.loaded / evt.total) * 100);
              setUploadingFlag(item.order_id, Math.max(1, Math.min(99, pct)));
            }
          },
        });
        toast.success(
          item.endpoint.endsWith("/deliver") ? "Delivery completed" : "Marked failed"
        );
        loadRef.current?.();
      } catch (e) {
        // 410 Gone = order was deleted server-side. Surface a specific
        // message so the driver knows to refresh (or contact dispatch)
        // instead of thinking the app is broken. The queue module will
        // drop the item to the graveyard immediately (see podQueue.js).
        if (e?.response?.status === 410) {
          toast.error(
            "This stop was deleted by dispatch. Refreshing your route…",
            { duration: 6000 }
          );
          // Also drop the local optimistic entry so the stop doesn't
          // stay stuck in a "failed" limbo after refresh.
          clearOptimistic(item.order_id);
          loadRef.current?.();
        }
        throw e;
      } finally {
        setUploadingFlag(item.order_id, false);
      }
    };
    attachUploader(uploader);
    startSweeper();
    const unsubscribe = subscribeQueue((items) => setPendingQ(items));

    // Graveyard subscriber — the queue drops items to the graveyard on hard
    // give-up or browser-storage corruption. Without this the driver's
    // optimistic green checkmark would stay forever and the backend would
    // never learn about the delivery (PROD INCIDENT 2026-08-25, East 08-25
    // stops 3 & 4). Clear the ghost state + surface a loud red toast so the
    // driver knows to re-capture POD.
    const unsubscribeGraveyard = subscribeGraveyardQueue((dead) => {
      if (!dead?.order_id) return;
      clearOptimistic(dead.order_id);
      const kind = dead.endpoint?.endsWith("/fail") ? "Failed-delivery" : "Delivery";
      const reason = dead.dropped_reason || dead.last_error || "unknown error";
      toast.error(
        `${kind} upload did NOT reach the server — please re-capture POD. (${reason})`,
        { duration: 15000, id: `graveyard-${dead.order_id}` }
      );
      loadRef.current?.();
    });

    return () => { unsubscribe?.(); unsubscribeGraveyard?.(); };
  }, []);

  // Surface ONE toast per persistently failing item so the driver knows the
  // queue is retrying without spamming. We track the ids we have already
  // warned about for the lifetime of the page. We include `last_error` in
  // the toast so the driver (or support looking over their shoulder) can
  // see WHY the upload is failing — e.g. "POD storage temporarily
  // unavailable (photo)" points at the object-storage service.
  const warnedRef = useRef(new Set());
  useEffect(() => {
    pendingQ.forEach((it) => {
      if (it.status === "failed" && it.attempts >= 1 && !warnedRef.current.has(it.id)) {
        warnedRef.current.add(it.id);
        const kind = it.endpoint.endsWith("/fail") ? "failed delivery" : "POD";
        const reason = it.last_error ? ` (${it.last_error})` : "";
        toast.warning(
          `Upload for ${kind} is retrying in background${reason} — we'll keep trying every 30 s.`,
          { duration: 8000 }
        );
      }
    });
  }, [pendingQ]);

  const load = () => {
    api.get("/routes", { params: { mine: true } }).then((r) => {
      setRoutes(r.data);
      // Route is picked by URL param. If the param is missing OR the route can't
      // be found in our list, auto-redirect to the routes-list landing page.
      if (!routeId) {
        const fallback = r.data.find((x) => x.status === "in_progress")
          || r.data.find((x) => x.status === "planned")
          || r.data.find((x) => x.status === "paused")
          || r.data[0];
        if (fallback) navigate(`/driver/route/${fallback.id}`, { replace: true });
        else navigate("/driver/routes", { replace: true });
        return;
      }
      const cur = r.data.find((x) => x.id === routeId);
      if (cur) {
        api.get(`/routes/${cur.id}`).then((rr) => setActive(rr.data));
      } else {
        setActive(null);
      }
    });
    api.get("/orders", { params: { mine: true } }).then((r) => setOrders(r.data));
  };

  // Reconcile the persisted optimistic cache against server truth on every
  // orders poll. Drop entries where the server has already caught up to a
  // terminal status (delivered / failed / picked_up / returned) so the
  // localStorage record does not grow unbounded and stale entries can't
  // resurrect after e.g. dispatch reverts a delivery. Also drop entries
  // whose order is no longer visible to this driver (route reassigned).
  useEffect(() => {
    if (!Object.keys(optimistic).length) return;
    const TERMINAL = new Set(["delivered", "failed", "picked_up", "returned"]);
    setOptimistic((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const oid of Object.keys(prev)) {
        const o = orders.find((x) => x.id === oid);
        if (!o) continue; // keep — order might be in-flight / not in this feed yet
        if (TERMINAL.has(o.status)) {
          delete next[oid];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [orders]);
  // Keep the queue uploader's reference fresh so it can refetch the route
  // after a background retry succeeds.
  loadRef.current = load;

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // Live GPS sharing — watches position while a route is in progress and shareLoc is on
  useEffect(() => {
    const inProgress = active?.status === "in_progress";
    if (!inProgress || !shareLoc) {
      if (watchIdRef.current && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return;
    }
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this device");
      return;
    }
    let lastSentAt = 0;
    const send = (lat, lng, accuracy) => {
      const now = Date.now();
      if (now - lastSentAt < 12000) return; // throttle to ~ every 12s
      lastSentAt = now;
      api.post("/driver/heartbeat", { lat, lng, accuracy, route_id: active.id }).catch(() => {});
      setMyLoc({ lat, lng, at: new Date().toISOString() });
    };
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => send(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy),
      (err) => console.warn("geo err", err),
      { enableHighAccuracy: true, maximumAge: 8000, timeout: 15000 }
    );
    return () => {
      if (watchIdRef.current && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [active?.id, active?.status, shareLoc]);

  const stopsWithStatus = useMemo(() => {
    if (!active) return [];
    return active.stops.map((s) => {
      const o = orders.find((x) => x.id === s.order_id);
      const itemsTxt = ((o?.items) || []).map((it) => (it?.name || "").toLowerCase()).join(" | ");
      // The optimistic entry wins ONLY while it represents a forward move
      // (assigned/in_transit → delivered/failed). Once the server reports
      // the same terminal state, we let the server be the source of truth.
      // For route-level PICKUP stops there's no matching order in `orders`,
      // so we MUST fall back to the route-stop's own status (e.g. "pending_pickup",
      // "picked_up") — otherwise the UI would keep showing the "Mark picked up"
      // button forever and the driver would think the app is frozen.
      const opt = optimistic[s.order_id]?.status;
      const serverStatus = o?.status || s.status || "assigned";
      // Server order is retry_pending when it's on this route as a 2nd
      // attempt. If the driver just took action (opt = delivered/failed),
      // the retry loop is closed locally too.
      const serverRetryPending = !!(o?.retry_pending ?? s.retry_pending);
      // For failed retries (serverStatus="failed" + retry_pending=true), the
      // opt can override to a new terminal state (delivered/failed) because
      // the stop is actionable this attempt.
      let effective;
      if (opt && serverStatus !== "delivered" && (serverStatus !== "failed" || serverRetryPending)) {
        effective = opt;
      } else {
        effective = serverStatus;
      }
      const retryPending = opt ? false : serverRetryPending;
      return {
        ...s,
        status: effective,
        uploading: uploading[s.order_id] || 0,
        customer_phone: o?.customer_phone,
        on_the_way_at: o?.on_the_way_at,
        copay: o?.copay,
        // Hard "signature required — do NOT leave at front door" flag. Read
        // from the order (source of truth); falls back to the stop copy so
        // legacy routes that already carry the flag keep working.
        mandatory_signature: !!(o?.mandatory_signature ?? s.mandatory_signature),
        notes: o?.notes,
        items: o?.items || [],
        failed_count: o?.failed_count || 0,
        // RETRY-RULE: a stop where the order lives in "failed" status but is
        // still on THIS route (retry_pending=True) is a 2nd-attempt delivery
        // — the driver must still be able to act on it (On the Way / Deliver
        // / Mark failed). We surface `is_retry` for the UI to decide.
        retry_pending: retryPending,
        is_retry: retryPending && effective === "failed",
        // Pharmacy attribution shown above the patient name so the driver
        // knows where the package came from without opening details. Use
        // the short nickname when set (e.g. "GoodPill") and fall back to
        // the legal name otherwise. Coming from /orders payload's
        // serialize_order() which already merges this on the doc.
        pharmacy_alias: o?.pharmacy_nickname || o?.pharmacy_name || "",
        pharmacy_nickname: o?.pharmacy_nickname || o?.pharmacy_name || "",
        is_refrigerated: itemsTxt.includes("refrigerated"),
        is_controlled: itemsTxt.includes("controlled"),
      };
    });
  }, [active, orders, optimistic, uploading]);

  // A stop is "done" when it has a POD outcome for THIS attempt. Failed
  // retries (status="failed" but retry_pending=True) are 2nd-attempt stops
  // brought in from a previous route — they still need action, so they DO
  // NOT count as done and stay in the "Pending" tab.
  const isStopDone = (s) => {
    if (s.status === "delivered" || s.status === "picked_up") return true;
    if (s.status === "failed") return !s.retry_pending;
    return false;
  };

  // Pending / Complete tab on the stops list.
  // - "pending"  → assigned + in_transit + failed-retry (open stops yet to be resolved)
  // - "complete" → delivered + failed (terminal stops with POD)
  const [stopsTab, setStopsTab] = useState("pending");
  const pendingCount = useMemo(
    () => stopsWithStatus.filter((s) => !isStopDone(s)).length,
    [stopsWithStatus]
  );
  const completeCount = useMemo(
    () => stopsWithStatus.filter(isStopDone).length,
    [stopsWithStatus]
  );
  const visibleStops = useMemo(() => {
    if (stopsTab === "complete") {
      return stopsWithStatus.filter(isStopDone);
    }
    return stopsWithStatus.filter((s) => !isStopDone(s));
  }, [stopsTab, stopsWithStatus]);

  // ─── 3:30 PM ET Clinic warning polling ──────────────────────────────
  // We ping every 30 s so the warning appears the moment the clock crosses
  // 15:30 ET even if the driver has been staring at the page since morning.
  useEffect(() => {
    const check = () => {
      if (!active || active.status !== "in_progress") return;
      // Compute wall-clock time in America/New_York (auto-handles EDT / EST DST).
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        weekday: "short", hour: "2-digit", minute: "2-digit", year: "numeric", month: "2-digit", day: "2-digit",
        hour12: false,
      }).formatToParts(new Date()).reduce((a, p) => (a[p.type] = p.value, a), {});
      const wd = parts.weekday; // Sun/Mon/…
      const hh = parseInt(parts.hour, 10);
      const mm = parseInt(parts.minute, 10);
      const dateKey = `${parts.year}-${parts.month}-${parts.day}`;
      const inWeekday = ["Mon", "Tue", "Wed", "Thu", "Fri"].includes(wd);
      // Trigger window: any time from 15:30 onward that same day (so the
      // warning still fires if the driver opens the app at 4 pm).
      const inWindow = inWeekday && (hh > 15 || (hh === 15 && mm >= 30));
      if (!inWindow) return;
      // Any stop whose priority is "clinic" and that isn't finished for the day.
      const openClinics = stopsWithStatus.filter(
        (s) => (s.priority || "").toLowerCase() === "clinic" && !isStopDone(s)
      );
      if (openClinics.length === 0) return;
      const flagKey = `srci:clinic-warn:${dateKey}:${active.id}`;
      if (localStorage.getItem(flagKey) === "1") return;
      setClinicWarn({ open: true, stops: openClinics, flagKey });
    };
    check();
    const t = setInterval(check, 30_000);
    return () => clearInterval(t);
  }, [active, stopsWithStatus]);

  const dismissClinicWarn = () => {
    if (clinicWarn.flagKey) {
      try { localStorage.setItem(clinicWarn.flagKey, "1"); } catch {}
    }
    setClinicWarn({ open: false, stops: [] });
  };

  // One-time alert when the driver first sees an active route containing special-handling stops
  const [alertedRouteId, setAlertedRouteId] = useState(null);
  useEffect(() => {
    if (!active?.id || alertedRouteId === active.id) return;
    if (!stopsWithStatus.length) return;
    const refrig = stopsWithStatus.filter((s) => s.is_refrigerated && !isStopDone(s));
    const ctrl = stopsWithStatus.filter((s) => s.is_controlled && !isStopDone(s));
    if (refrig.length === 0 && ctrl.length === 0) {
      setAlertedRouteId(active.id);
      return;
    }
    const parts = [];
    if (refrig.length) parts.push(`${refrig.length} REFRIGERATED`);
    if (ctrl.length) parts.push(`${ctrl.length} CONTROLLED`);
    toast.warning(
      `Heads up: this route has ${parts.join(" + ")} ${refrig.length + ctrl.length === 1 ? "delivery" : "deliveries"}.`,
      { description: "Handle with extra care and follow your pharmacy's protocol.", duration: 8000 }
    );
    setAlertedRouteId(active.id);
  }, [active?.id, stopsWithStatus, alertedRouteId]);

  // Per-stop alert when the driver taps Deliver / Fail on a special-handling order
  const flashSpecialAlert = (s) => {
    if (s.is_refrigerated) {
      toast.warning("REFRIGERATED delivery", {
        description: `${s.customer_name} — keep cold chain intact until handoff.`,
        duration: 6000,
      });
    }
    if (s.is_controlled) {
      toast.warning("CONTROLLED medication", {
        description: `${s.customer_name} — verify recipient ID and capture signature.`,
        duration: 6000,
      });
    }
  };

  const startRoute = async () => {
    await api.post(`/routes/${active.id}/start`);
    toast.success("Route started — drive safe!");
    load();
  };

  const pauseRoute = async () => {
    try {
      const { data } = await api.post(`/routes/${active.id}/pause`);
      toast.success(data?.status === "paused" ? "Route paused" : "Route resumed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pause route");
    }
  };

  const [confirmFinish, setConfirmFinish] = useState(false);
  const finishRoute = async () => {
    try {
      await api.post(`/routes/${active.id}/finish`);
      toast.success("Route completed");
      setConfirmFinish(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not finish route");
    }
  };

  // Google Maps "navigate" deep link — opens turn-by-turn in the native Google Maps
  // app on iOS / Android, or maps.google.com in any browser as a fallback.
  const googleMapsUrl = (lat, lng) =>
    `https://www.google.com/maps/dir/?api=1&travelmode=driving&dir_action=navigate&destination=${lat},${lng}`;

  const failSubmit = async () => {
    if (!failOrder) return;
    if (!failReason.trim()) { toast.error("Add a reason"); return; }
    if (!failPhoto) { toast.error("Please capture a photo to document the failure."); return; }

    // ---- OPTIMISTIC ENQUEUE ---------------------------------------------
    // Persist the submission to IndexedDB FIRST so a network blip during
    // the POST can't lose the work. Tear the dialog down immediately and
    // let the queue sweeper retry every 30 s until it lands.
    // PRODUCTION BUG FIX (2026-08-12): route stops carry `order_id`, NOT `id`
    // (see backend `serialize_route`). Reading `failOrder.id` resolved to
    // `undefined` → POST to `/orders/undefined/fail` → backend returned 410
    // Gone → driver saw "This stop was deleted by dispatch" for EVERY fail
    // attempt even though the order was very much alive.
    const orderId = failOrder.order_id;
    const parts = { reason: failReason, photo: failPhoto };
    setFailOrder(null);
    setFailReason("");
    setFailPhoto(null);
    setFailPhotoPreview(null);
    markOptimistic(orderId, "failed");
    try {
      await queuePOD({ order_id: orderId, endpoint: `/orders/${orderId}/fail`, parts });
      // Trigger an immediate drain — the sweeper would otherwise wait up to 30 s.
      void drainQueue();
    } catch (e) {
      clearOptimistic(orderId);
      toast.error("Could not queue the failure report — please retry.");
    }
  };

  // POD background upload: invoked synchronously by PODCapture once local
  // validation passes. We persist the parts to IndexedDB, tear down the
  // sheet, and the queue sweeper does the rest.
  const handlePodUpload = async (orderId, parts) => {
    const requireSignature = (podOrder?.deliver_type || "").toLowerCase() !== "pickup";
    setPodOrder(null);
    markOptimistic(orderId, "delivered");
    try {
      await queuePOD({ order_id: orderId, endpoint: `/orders/${orderId}/deliver`, parts, requireSignature });
      void drainQueue();
    } catch (e) {
      clearOptimistic(orderId);
      toast.error(e?.message ? `Could not save the POD — ${e.message}` : "Could not queue the POD — please retry.", { duration: 10000 });
    }
  };

  const onFailPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Compress before storing so the upload payload matches POD photos
    // (4–8 MB → 150–350 KB). The Sheet has no spinner of its own, but the
    // compressor finishes in <500 ms which is imperceptible.
    const compressed = await compressImage(f);
    setFailPhoto(compressed);
    setFailPhotoPreview(URL.createObjectURL(compressed));
  };
  const clearFailPhoto = () => {
    setFailPhoto(null);
    setFailPhotoPreview(null);
  };

  // --------- "All routes" pill (single-line CTA back to the list page) ----
  const AllRoutesPill = () => (
    <Link
      to="/driver/routes"
      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-secondary hover:bg-secondary/80 transition-colors text-muted-foreground hover:text-foreground"
      data-testid="driver-all-routes-link"
    >
      <ArrowLeft className="h-3.5 w-3.5" /> All routes
    </Link>
  );

  if (!active) {
    return (
      <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="driver-empty">
        <div className="mb-4"><AllRoutesPill /></div>
        <h1 className="text-3xl font-display font-bold tracking-tight">My route</h1>
        <Card className="mt-6 p-8 text-center rounded-xl border-border bg-card/80">
          <div className="text-5xl mb-3">🛻</div>
          <p className="text-muted-foreground">No active route assigned to you yet.</p>
          <p className="text-xs text-muted-foreground mt-2">Sit tight — your dispatcher will assign one shortly.</p>
        </Card>
      </div>
    );
  }

  const completedCount = stopsWithStatus.filter(isStopDone).length;
  const fixUntil = fixDeadline(active);

  const reopenStop = async (s) => {
    if (!window.confirm(`Reopen stop #${s.sequence} (${s.customer_name})? Its POD will be cleared and the stop moves back to Pending.`)) return;
    try {
      await api.post(`/orders/${s.order_id}/reopen`);
      clearOptimistic(s.order_id);
      toast.success(`Stop #${s.sequence} reopened — it's back in Pending`);
      setStopsTab("pending");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto pb-24" data-testid="driver-page">
      <div className="mb-3"><AllRoutesPill /></div>
      <header className="mb-4">
        <h1 className="text-2xl lg:text-3xl font-display font-bold tracking-tight">{active.label}</h1>
        <p className="text-sm text-muted-foreground">
          {active.stops.length} stops · {fmtDistance(active.distance_km)} · ~{fmtEta(active.duration_min, active.stops.length)}
        </p>
        {pendingQ.length > 0 && (
          <button
            type="button"
            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-900 text-xs font-medium border border-amber-300/60 hover:bg-amber-200 active:scale-[.98] transition"
            data-testid="driver-pending-uploads-pill"
            title="Tap to retry uploading now"
            onClick={async () => {
              // Drivers can only retry — the local queue is never cleared from the app
              // so a pending POD can't be lost.
              toast.info("Retrying uploads now…");
              await drainQueue();
            }}
          >
            {pendingQ.some((it) => it.status === "failed") ? (
              <AlertTriangle className="h-3.5 w-3.5" />
            ) : (
              <CloudUpload className="h-3.5 w-3.5 animate-pulse" />
            )}
            {pendingQ.length} {pendingQ.length === 1 ? "upload" : "uploads"} pending
          </button>
        )}
        <div className="mt-2 flex items-center gap-3 text-xs">
          <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-accent transition-all" style={{ width: `${(completedCount / active.stops.length) * 100}%` }} />
          </div>
          <span className="font-mono-num font-semibold">{completedCount}/{active.stops.length}</span>
        </div>
      </header>

      <MapView start={active.start} stops={stopsWithStatus} polyline={active.polyline} height={300} driverLocation={myLoc} routeStatus={active.status} fitToStopsOnly />

      {active.status === "in_progress" && (
        <Card className="mt-3 p-3 rounded-xl border-border bg-card/80 flex items-center gap-3" data-testid="gps-toggle-card">
          <div className={`h-9 w-9 rounded-full grid place-items-center ${shareLoc ? "bg-[#3a86ff]/15 text-[#1f4ea3]" : "bg-secondary text-muted-foreground"}`}>
            <MapPin className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium">Share my location</div>
            <div className="text-xs text-muted-foreground">
              {shareLoc
                ? (myLoc ? `Sharing live • ${fmtTime(myLoc.at)}` : "Awaiting GPS signal…")
                : "Dispatcher won't see your position"}
            </div>
          </div>
          <Switch checked={shareLoc} onCheckedChange={setShareLoc} data-testid="gps-toggle" />
        </Card>
      )}

      <div className="mt-4 grid grid-cols-3 gap-2" data-testid="driver-route-controls">
        <Button
          onClick={startRoute}
          disabled={active.status !== "planned"}
          className="rounded-full h-12 disabled:opacity-40"
          data-testid="start-route-driver"
        >
          <Play className="h-4 w-4 mr-1.5" /> Start
        </Button>
        <Button
          onClick={pauseRoute}
          disabled={active.status !== "in_progress" && active.status !== "paused"}
          className={`rounded-full h-12 disabled:opacity-40 ${active.status === "paused" ? "bg-[#3a86ff] hover:bg-[#1f4ea3] text-white" : "bg-[#facc15] hover:bg-[#eab308] text-black"}`}
          data-testid="pause-route-driver"
        >
          <Pause className="h-4 w-4 mr-1.5" /> {active.status === "paused" ? "Resume" : "Pause"}
        </Button>
        <Button
          onClick={() => setConfirmFinish(true)}
          disabled={active.status !== "in_progress" && active.status !== "paused"}
          className="rounded-full h-12 bg-accent hover:bg-accent/90 text-accent-foreground disabled:opacity-40"
          data-testid="finish-route-driver"
        >
          <Flag className="h-4 w-4 mr-1.5" /> Finish
        </Button>
      </div>
      {active.status === "in_progress" && (
        <div className="mt-2 text-xs text-primary font-medium">● Route in progress</div>
      )}
      {active.status === "paused" && (
        <div className="mt-2 text-xs text-[#facc15] font-medium">⏸ Route paused</div>
      )}
      {active.status === "completed" && (
        <div className="mt-2 text-xs text-accent font-medium">✓ Route completed</div>
      )}
      {active.status === "completed" && fixUntil && fixUntil !== Infinity && (
        <div className="mt-2 text-xs rounded-lg bg-amber-50 border border-amber-300/70 text-amber-900 px-3 py-2" data-testid="fix-window-banner">
          You can still fix completed stops until <span className="font-semibold">{fmtTime(new Date(fixUntil).toISOString())}</span> (Complete tab → Fix this stop).
        </div>
      )}

      {/* Guardrail: until the driver hits Start, all stop-level actions are
          disabled and this banner explains why. Ensures Duration Time on
          the Timesheet is measured from the actual Start → Finish window. */}
      {active.status === "planned" && (
        <div
          className="mt-4 rounded-xl border border-amber-300/70 bg-amber-50 text-amber-900 px-4 py-3 text-sm flex items-start gap-2"
          data-testid="route-not-started-banner"
        >
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Please note this Route has not started yet.</div>
            <div className="text-[12px] mt-0.5 opacity-90">
              Tap <span className="font-semibold">Start</span> above to begin the route. Stop actions are disabled until then.
            </div>
          </div>
        </div>
      )}

      <ol className="mt-5 space-y-3" data-testid="driver-stops">
        <li className="px-3 py-2.5 rounded-lg bg-primary/8 border border-primary/15 text-xs text-primary flex items-start gap-2" data-testid="stops-order-hint">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            <span className="font-semibold">Tip:</span> The sequence below is just a suggestion — you can deliver any stop in any order that suits your trip.
          </span>
        </li>

        {/* Pending / Complete segmented toggle */}
        <li
          className="inline-flex w-full sm:w-auto rounded-full bg-secondary/60 p-1 self-start"
          role="radiogroup"
          aria-label="Filter stops"
          data-testid="stops-filter"
        >
          {[
            { v: "pending", label: "Pending", count: pendingCount },
            { v: "complete", label: "Complete", count: completeCount },
          ].map((opt) => {
            const activeTab = stopsTab === opt.v;
            return (
              <button
                key={opt.v}
                type="button"
                role="radio"
                aria-checked={activeTab}
                onClick={() => setStopsTab(opt.v)}
                className={`flex-1 sm:flex-none px-4 h-9 text-[12px] font-semibold uppercase tracking-wider rounded-full transition-colors inline-flex items-center justify-center gap-1.5 ${
                  activeTab
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`stops-filter-${opt.v}`}
              >
                {opt.label}
                <span className={`text-[10px] font-mono-num rounded-full px-1.5 ${activeTab ? "bg-white/20 text-white" : "bg-foreground/10 text-foreground/70"}`}>
                  {opt.count}
                </span>
              </button>
            );
          })}
        </li>

        {visibleStops.length === 0 && (
          <li className="px-3 py-6 text-center text-sm text-muted-foreground rounded-lg border border-dashed border-border" data-testid="stops-empty">
            {stopsTab === "complete" ? "No deliveries completed yet." : "No pending stops — great work!"}
          </li>
        )}

        {visibleStops.map((s) => {
          // Guardrail: no stop-level actions before the driver taps Start.
          // We keep the cards visible (so the driver can review the plan)
          // but every action button below is disabled while `planned`.
          const routeStarted = active.status === "in_progress" || active.status === "paused";
          return (
          <Card key={s.order_id || `pickup-${s.sequence}`} className={`p-4 rounded-xl border-border bg-card/90 ${(s.status === "delivered" || s.status === "picked_up" || (s.status === "failed" && !s.is_retry)) ? "opacity-60" : ""} ${s.is_pickup ? "ring-2 ring-amber-300/60" : ""} ${s.is_retry ? "ring-2 ring-destructive/60" : ""}`} data-testid={`driver-stop-${s.order_id || "pickup-" + s.sequence}`}>
            <div className="flex items-start gap-3">
              <div className={`h-9 w-9 rounded-full grid place-items-center font-bold font-mono-num text-sm shrink-0 ${
                s.is_pickup ? "bg-amber-500 text-white" :
                s.status === "delivered" ? "bg-accent text-accent-foreground" :
                (s.status === "failed" && !s.is_retry) ? "bg-destructive text-destructive-foreground" :
                s.is_retry ? "bg-destructive text-destructive-foreground ring-2 ring-offset-1 ring-destructive" :
                "bg-primary text-primary-foreground"
              }`}>
                {s.is_pickup ? "↑" : s.status === "delivered" ? "✓" : (s.status === "failed" && !s.is_retry) ? "✕" : s.sequence}
              </div>
              <div className="flex-1 min-w-0">
                {s.is_pickup && (
                  <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-amber-900 bg-amber-100 px-2 py-0.5 rounded-full mb-1" data-testid={`stop-pickup-badge-${s.sequence}`}>
                    PICKUP{s.pickup_qty ? ` · ${s.pickup_qty} pkg${s.pickup_qty === 1 ? "" : "s"}` : ""}
                  </div>
                )}
                {!s.is_pickup && s.pharmacy_alias && (
                  <div
                    className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-0.5"
                    data-testid={`stop-pharmacy-alias-${s.order_id}`}
                  >
                    {s.pharmacy_alias}
                  </div>
                )}
                <div className="font-semibold">{s.customer_name}</div>
                <div className="text-sm text-muted-foreground">{s.address}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {s.is_retry && (
                    <span
                      className="inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-destructive/15 text-destructive border border-destructive/40"
                      title={`2nd delivery attempt — previously failed${s.failed_count > 1 ? ` (${s.failed_count}× total)` : ""}`}
                      data-testid={`badge-retry-${s.order_id}`}
                    >
                      ↻ 2ND ATTEMPT{s.failed_count > 1 ? ` (${s.failed_count}×)` : ""}
                    </span>
                  )}
                  {(s.deliver_type || "").toLowerCase() === "pickup" && (
                    <span
                      className="inline-flex items-center text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: "#7C3AED", color: "#ffffff" }}
                      title="Pick Up order — collect from this address"
                      data-testid={`badge-pickup-${s.order_id}`}
                    >
                      ↑ Pick Up
                    </span>
                  )}
                  {s.priority && s.priority !== "normal" && (
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">{s.priority.replace("_", " ")}</span>
                  )}
                </div>
              </div>
              {/* Right-side handling flags (refrigerated / controlled) — moved here so customer name stays prominent. */}
              {(s.is_refrigerated || s.is_controlled) && (
                <div className="flex flex-col items-end gap-1 shrink-0" data-testid={`badges-handling-${s.order_id}`}>
                  {s.is_refrigerated && (
                    <span
                      className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#3a86ff] text-white"
                      title="Refrigerated — keep cold"
                      data-testid={`badge-refrigerated-${s.order_id}`}
                    >
                      <Snowflake className="h-3.5 w-3.5" />
                    </span>
                  )}
                  {s.is_controlled && (
                    <span
                      className="inline-flex items-center text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full bg-destructive text-destructive-foreground"
                      title="Controlled substance (CII) — ID required at delivery"
                      data-testid={`badge-controlled-${s.order_id}`}
                    >
                      CII
                    </span>
                  )}
                </div>
              )}
            </div>
            {s.mandatory_signature && !s.is_pickup && (
              <div
                className="mt-3 rounded-lg border-2 border-destructive bg-destructive/10 px-3 py-2 flex items-start gap-2"
                data-testid={`stop-mandatory-signature-${s.order_id}`}
              >
                <PenLine className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
                <div className="text-[12px] leading-snug text-destructive">
                  <div className="font-bold uppercase tracking-wider text-[11px]">Mandatory Signature</div>
                  Do NOT leave at the front door — the pharmacy requires the patient's signature at handoff.
                </div>
              </div>
            )}
            {s.notes && (
              <div
                className="mt-3 rounded-lg border-l-4 border-[#FF6D00] bg-[#fff4d6] px-3 py-2 text-xs text-[#5a3700]"
                data-testid={`stop-notes-${s.order_id}`}
              >
                <div className="font-semibold text-[10px] uppercase tracking-wider text-[#9c4500] mb-0.5">Delivery notes</div>
                <div className="whitespace-pre-wrap leading-snug">{s.notes}</div>
              </div>
            )}
            {s.is_pickup && s.status !== "picked_up" && (
              // Pickup-stop UI: simplified — driver just navigates and taps
              // "Mark picked up". No POD modal, no fail flow.
              <div className="mt-3 grid grid-cols-2 gap-2">
                <a href={routeStarted ? googleMapsUrl(s.lat, s.lng) : undefined} target="_blank" rel="noreferrer" onClick={(e) => { if (!routeStarted) { e.preventDefault(); toast.warning("Start the route first"); } }}>
                  <Button variant="outline" disabled={!routeStarted} className="w-full h-11 rounded-full disabled:opacity-40" data-testid={`nav-pickup-${s.sequence}`}>
                    <Navigation className="h-4 w-4 mr-1.5" /> Open in Google Maps
                  </Button>
                </a>
                <Button
                  onClick={async () => {
                    if (!routeStarted) { toast.warning("Start the route first"); return; }
                    try {
                      await api.post(`/routes/${active.id}/stops/${s.sequence}/picked-up`);
                      toast.success(`Picked up at ${s.customer_name}`);
                      load();
                    } catch (e) {
                      toast.error(formatApiError(e));
                    }
                  }}
                  disabled={!routeStarted}
                  className="h-11 rounded-full bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-40"
                  data-testid={`pickup-done-${s.sequence}`}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark picked up
                </Button>
              </div>
            )}
            {s.is_pickup && s.status === "picked_up" && (
              <div className="mt-2 text-xs text-amber-700 font-medium" data-testid={`pickup-done-badge-${s.sequence}`}>
                ↑ Picked up{s.pickup_qty ? ` · ${s.pickup_qty} pkg${s.pickup_qty === 1 ? "" : "s"}` : ""}
              </div>
            )}
            {!s.is_pickup && s.status !== "delivered" && !(s.status === "failed" && !s.is_retry) && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <a href={routeStarted ? googleMapsUrl(s.lat, s.lng) : undefined} target="_blank" rel="noreferrer" onClick={(e) => { if (!routeStarted) { e.preventDefault(); toast.warning("Start the route first"); } }} className="col-span-2">
                  <Button variant="outline" disabled={!routeStarted} className="w-full h-11 rounded-full disabled:opacity-40" data-testid={`nav-google-${s.order_id}`}>
                    <Navigation className="h-4 w-4 mr-1.5" /> Open in Google Maps
                  </Button>
                </a>
                <a href={routeStarted ? `tel:${s.customer_phone}` : undefined} onClick={(e) => { if (!routeStarted) { e.preventDefault(); toast.warning("Start the route first"); } }}>
                  <Button variant="outline" disabled={!routeStarted} className="w-full h-11 rounded-full disabled:opacity-40" data-testid={`call-customer-${s.order_id}`}>
                    <Phone className="h-4 w-4 mr-1.5" /> Call
                  </Button>
                </a>
                <a href={routeStarted ? `sms:${s.customer_phone}` : undefined} onClick={(e) => { if (!routeStarted) { e.preventDefault(); toast.warning("Start the route first"); } }}>
                  <Button variant="outline" disabled={!routeStarted} className="w-full h-11 rounded-full disabled:opacity-40" data-testid={`text-customer-${s.order_id}`}>
                    <MessageSquare className="h-4 w-4 mr-1.5" /> Text
                  </Button>
                </a>
                <Button
                  onClick={async () => {
                    if (!routeStarted) { toast.warning("Start the route first"); return; }
                    try {
                      await api.post(`/orders/${s.order_id}/on-the-way`);
                      toast.success("Patient notified — you're on the way");
                      load();
                    } catch (e) {
                      toast.error("Could not send notification");
                    }
                  }}
                  disabled={!routeStarted || !!s.on_the_way_at}
                  className="col-span-2 h-11 rounded-full bg-[#3a86ff] hover:bg-[#1f4ea3] text-white disabled:opacity-40"
                  data-testid={`on-the-way-${s.order_id}`}
                >
                  <Navigation className="h-4 w-4 mr-1.5" />
                  {s.on_the_way_at ? "Patient notified ✓" : "On the Way"}
                </Button>
                <Button
                  onClick={() => { if (!routeStarted) { toast.warning("Start the route first"); return; } flashSpecialAlert(s); setPodOrder(s); }}
                  disabled={!routeStarted}
                  className="col-span-2 h-12 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground disabled:opacity-40"
                  data-testid={`deliver-${s.order_id}`}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> Deliver & capture POD
                </Button>
                <Button
                  onClick={() => { if (!routeStarted) { toast.warning("Start the route first"); return; } flashSpecialAlert(s); setFailOrder(s); }}
                  disabled={!routeStarted}
                  className="col-span-2 h-11 rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground disabled:opacity-40"
                  data-testid={`fail-${s.order_id}`}
                >
                  <XCircle className="h-4 w-4 mr-1.5" /> Mark failed
                </Button>
              </div>
            )}
            {s.status === "delivered" && (
              <div className="mt-2 text-xs text-accent font-medium flex items-center gap-1.5" data-testid={`stop-delivered-badge-${s.order_id}`}>
                ✓ Delivered{s.uploading > 0 && <span className="text-muted-foreground font-normal" data-testid={`stop-upload-progress-${s.order_id}`}>· uploading proof… {s.uploading}%</span>}
              </div>
            )}
            {s.status === "failed" && !s.is_retry && (
              <div className="mt-2 text-xs text-destructive font-medium flex items-center gap-1.5">
                ✕ Failed{s.uploading > 0 && <span className="text-muted-foreground font-normal" data-testid={`stop-upload-progress-${s.order_id}`}>· uploading evidence… {s.uploading}%</span>}
              </div>
            )}
            {!s.is_pickup && fixUntil && !s.uploading && (s.status === "delivered" || (s.status === "failed" && !s.is_retry)) && (
              <StopFixActions
                stop={s}
                onDeliver={(st) => { flashSpecialAlert(st); setPodOrder(st); }}
                onFail={(st) => setFailOrder(st)}
                onReopen={reopenStop}
              />
            )}
            {s.status === "returned" && <div className="mt-2 text-xs text-[#7C3AED] font-medium">↩ Returned</div>}
          </Card>
          );
        })}
      </ol>

      <Dialog open={confirmFinish} onOpenChange={setConfirmFinish}>
        <DialogContent>
          <DialogHeader><DialogTitle>Finish route?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will close the route as completed. Any remaining stops will stay as-is — make sure you've marked each delivery before finishing.
          </p>
          <Button onClick={finishRoute} className="rounded-full h-12 mt-2 bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="confirm-finish-route">
            <Flag className="h-4 w-4 mr-1.5" /> Yes, finish route
          </Button>
        </DialogContent>
      </Dialog>

      <Sheet open={!!podOrder} onOpenChange={(o) => !o && setPodOrder(null)}>
        <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto" data-testid="pod-sheet">
          <SheetHeader>
            <SheetTitle>{(podOrder?.deliver_type || "").toLowerCase() === "pickup" ? "Proof of pickup" : "Proof of delivery"}</SheetTitle>
            <div className="text-sm text-muted-foreground">{podOrder?.customer_name} · {podOrder?.address}</div>
          </SheetHeader>
          {podOrder && (
            <div className="pt-4">
              <PODCapture
                order={{ id: podOrder.order_id, copay: podOrder.copay, items: podOrder.items || [] }}
                onSubmitInBackground={handlePodUpload}
                requireSignature={(podOrder.deliver_type || "").toLowerCase() !== "pickup"}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={!!failOrder} onOpenChange={(o) => { if (!o) { setFailOrder(null); setFailReason(""); setFailPhoto(null); setFailPhotoPreview(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mark delivery as failed</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Reason</Label>
              <Textarea value={failReason} onChange={(e) => setFailReason(e.target.value)} placeholder="Recipient not available, wrong address..." data-testid="fail-reason" />
            </div>
            <div>
              <Label>Photo documenting the failure</Label>
              <div className="mt-1.5 relative">
                <label className="flex items-center justify-center gap-2 h-32 rounded-xl border-2 border-dashed border-border bg-secondary/40 hover:bg-secondary cursor-pointer transition-colors" data-testid="fail-photo-label">
                  {failPhotoPreview ? (
                    <img src={failPhotoPreview} alt="Fail evidence" className="h-full w-full object-cover rounded-xl" />
                  ) : (
                    <div className="flex flex-col items-center text-muted-foreground">
                      <Camera className="h-6 w-6" />
                      <span className="text-sm mt-1">Tap to add photo</span>
                    </div>
                  )}
                  <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={onFailPhoto} data-testid="fail-photo-input" />
                </label>
                {failPhotoPreview && (
                  <button
                    type="button"
                    onClick={clearFailPhoto}
                    className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80"
                    data-testid="fail-photo-clear"
                    aria-label="Remove photo"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
            <Button onClick={failSubmit} className="w-full rounded-full h-12" data-testid="fail-submit">
              Confirm failure
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 3:30 PM ET Clinic-still-open warning */}
      <Dialog open={clinicWarn.open} onOpenChange={(o) => { if (!o) dismissClinicWarn(); }}>
        <DialogContent className="max-w-sm" data-testid="clinic-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" /> Clinic stops still open
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            {/* eslint-disable-next-line react/no-unescaped-entities */}
            <p>
              It's after <strong>3:30 PM ET</strong> and your route still has{" "}
              <strong>{clinicWarn.stops.length}</strong> clinic {clinicWarn.stops.length === 1 ? "stop" : "stops"} that {clinicWarn.stops.length === 1 ? "hasn't" : "haven't"} been delivered yet. Clinics typically close at the end of business day — please prioritize these deliveries or contact dispatch if a rescheduling is required.
            </p>
            <ul className="rounded-lg border border-border divide-y divide-border max-h-52 overflow-y-auto" data-testid="clinic-warning-list">
              {clinicWarn.stops.map((s) => (
                <li key={s.order_id} className="px-3 py-2 text-xs" data-testid={`clinic-warning-stop-${s.order_id}`}>
                  <div className="font-medium">Stop #{s.sequence} · {s.customer_name}</div>
                  <div className="text-muted-foreground truncate">{s.address}</div>
                </li>
              ))}
            </ul>
          </div>
          <Button onClick={dismissClinicWarn} className="w-full rounded-full h-11 mt-2" data-testid="clinic-warning-ok">
            OK
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
